import moment from "moment-timezone";
import { toEpochMillisForDisplay } from "~/helpers/utils";
import { ServiceInstance } from "~/types/serviceInstance";
import {
  AggregationMode,
  ServiceKpiMetrics,
  ServiceKPIs,
  ServiceKpiDisplayMetricKey,
  ServiceKpiResolutionPeriod,
  TenantType,
} from "~/types/serviceKpi";
import { Tenant } from "~/types/tenant";
import { TenantTree } from "~/store/context/serviceInstanceSlice";

/** Managed By dropdown: all tenants, Highway9-only, or a specific MSP partner id. */
export type ManagedByFilter = "all" | "highway9" | string;

export const DEFAULT_RESOLUTION_SECONDS = 86400 * 7; // 1 week

/** Data bucket size (seconds) for each selectable aggregation period. */
export const RESOLUTION_SECONDS_BY_PERIOD: Record<ServiceKpiResolutionPeriod, number> = {
  "1day": 86400,
  "1week": 86400 * 7,
  "1month": 86400 * 30,
  "1quarter": 86400 * 90,
};

export function getResolutionSeconds(period: ServiceKpiResolutionPeriod = "1week"): number {
  return RESOLUTION_SECONDS_BY_PERIOD[period] ?? DEFAULT_RESOLUTION_SECONDS;
}

/** PromQL range vector (e.g. `[7d]`) matching each selectable aggregation period. */
export const PROMQL_RANGE_BY_RESOLUTION_PERIOD: Record<ServiceKpiResolutionPeriod, string> = {
  "1day": "1d",
  "1week": "7d",
  "1month": "30d",
  "1quarter": "90d",
};

export function getPromqlRange(period: ServiceKpiResolutionPeriod = "1week"): string {
  return PROMQL_RANGE_BY_RESOLUTION_PERIOD[period] ?? PROMQL_RANGE_BY_RESOLUTION_PERIOD["1week"];
}

/** Date-only label format (summary table columns). */
export const KPI_BUCKET_DATE_FORMAT = "DD MMM YY";

/** Date + time label for chart tooltips. */
export const KPI_BUCKET_DATETIME_FORMAT = "DD MMM YY h:mm A";

/** Compact date + time for chart x-axis (line break between time and date). */
export const KPI_BUCKET_AXIS_DATETIME_FORMAT = "h:mm a<br />DD MMM YY";

/** Formats a single API bucket timestamp for chart axis and tooltip labels. */
export function formatApiBucketLabel(
  bucketTimestamp: number,
  timeZone: string,
  format: string = KPI_BUCKET_DATETIME_FORMAT
): string {
  const startMs = toEpochMillisForDisplay(bucketTimestamp);
  return moment(startMs).tz(timeZone).format(format);
}

/** Formats a weekly bucket end timestamp as a calendar week range (start → end). */
export function formatBucketDateRange(
  bucketEndTimestamp: number,
  intervalSeconds: number,
  timeZone: string,
): string {
  const endMs = toEpochMillisForDisplay(bucketEndTimestamp);
  const endDay = moment(endMs).tz(timeZone).startOf("day");
  const startDay = endDay.clone().subtract(intervalSeconds, "seconds");
  const dateFormat = "DD MMM YY";
  return `${startDay.format(dateFormat)} - ${endDay.format(dateFormat)}`;
}

/** Week range label for KPI line charts (API bucket end → start of bucket week). */
export function formatKpiWeekRangeLabel(
  bucketTimestamp: number,
  timeZone: string,
  intervalSeconds: number = DEFAULT_RESOLUTION_SECONDS,
  multiline = false
): string {
  const range = formatBucketDateRange(bucketTimestamp, intervalSeconds, timeZone);
  return multiline ? range.replace(" - ", "<br />") : range;
}

export type MetricSeriesPoint = [number, number];

const KPI_PAUSE_STATUS_FILTER = 'pauseStatus=~"UNPAUSED|PAUSE|UNPAUSE"';
const KPI_DEVICE_PAUSE_STATUS_FILTER = 'pauseStatus="UNPAUSED"';

/** Every base KPI metric (vmc/edge/radio/device/coverage/site info) carries a
 * `serviceInstanceType` label (CX/EX/CX-KPI); append it when scoped by Mobile Cloud Type. */
function scopedKpiSelector(baseFilter: string, tenantType: "all" | TenantType): string {
  if (tenantType === "all") {
    return `{${baseFilter}}`;
  }
  const typeFilter = `serviceInstanceType="${tenantType}"`;
  return baseFilter ? `{${baseFilter},${typeFilter}}` : `{${typeFilter}}`;
}

// Base KPI formula builders; each respects the Mobile Cloud Type filter.
// last_over_time([65m]): overrides VM's default max(step,5m) staleness
export function getVmcKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `count(last_over_time(service_instance_info${scopedKpiSelector(KPI_PAUSE_STATUS_FILTER, tenantType)}[65m]))`;
}

export function getEdgesKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `count(last_over_time(edge_info${scopedKpiSelector(KPI_PAUSE_STATUS_FILTER, tenantType)}[65m]))`;
}

export function getRadiosKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `count(last_over_time(radio_info${scopedKpiSelector(KPI_PAUSE_STATUS_FILTER, tenantType)}[65m]))`;
}

export function getSitesKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `sum(last_over_time(radio_site_info${scopedKpiSelector(KPI_PAUSE_STATUS_FILTER, tenantType)}[65m]))`;
}

export function getSqftKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `sum(last_over_time(coverage_area_info${scopedKpiSelector(KPI_PAUSE_STATUS_FILTER, tenantType)}[65m]))`;
}

export function getIndoorSqftKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `sum(last_over_time(indoor_coverage_area_info${scopedKpiSelector(KPI_PAUSE_STATUS_FILTER, tenantType)}[65m]))`;
}

export function getOutdoorSqftKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `sum(last_over_time(outdoor_coverage_area_info${scopedKpiSelector(KPI_PAUSE_STATUS_FILTER, tenantType)}[65m]))`;
}

export function getDevicesTotalKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `sum(last_over_time(device_info${scopedKpiSelector(KPI_DEVICE_PAUSE_STATUS_FILTER, tenantType)}[65m]))`;
}

export function getDevicesConnectedKpiFormula(tenantType: "all" | TenantType = "all"): string {
  const filter = `status="CONNECTED",${KPI_DEVICE_PAUSE_STATUS_FILTER}`;
  return `sum(last_over_time(device_info${scopedKpiSelector(filter, tenantType)}[65m]))`;
}

const KPI_BASE_FORMULA_KEYS = [
  "vmc",
  "edgesTotal",
  "radiosTotal",
  "devicesConnected",
  "devicesTotal",
  "sites",
  "sqft",
  "indoorSqft",
  "outdoorSqft",
] as const;

const KPI_BASE_FORMULA_BUILDERS: Record<
  (typeof KPI_BASE_FORMULA_KEYS)[number],
  (tenantType: "all" | TenantType) => string
> = {
  vmc: getVmcKpiFormula,
  edgesTotal: getEdgesKpiFormula,
  radiosTotal: getRadiosKpiFormula,
  devicesConnected: getDevicesConnectedKpiFormula,
  devicesTotal: getDevicesTotalKpiFormula,
  sites: getSitesKpiFormula,
  sqft: getSqftKpiFormula,
  indoorSqft: getIndoorSqftKpiFormula,
  outdoorSqft: getOutdoorSqftKpiFormula,
};

// Extension PromQL formula builders; each respects the Mobile Cloud Type filter and the
// selected Aggregation Period (range vector: 1day→[1d], 1week→[7d], 1month→[30d], 1quarter→[90d]).
function getUploadTonnageKpiFormula(
  tenantType: "all" | TenantType = "all",
  resolutionPeriod: ServiceKpiResolutionPeriod = "1week"
): string {
  return `sum(sum_over_time(carrier_edge_upload_bytes${scopedKpiSelector("", tenantType)}[${getPromqlRange(resolutionPeriod)}]))`;
}

function getDownloadTonnageKpiFormula(
  tenantType: "all" | TenantType = "all",
  resolutionPeriod: ServiceKpiResolutionPeriod = "1week"
): string {
  return `sum(sum_over_time(carrier_edge_download_bytes${scopedKpiSelector("", tenantType)}[${getPromqlRange(resolutionPeriod)}]))`;
}

function getVolteCallsKpiFormula(
  tenantType: "all" | TenantType = "all",
  resolutionPeriod: ServiceKpiResolutionPeriod = "1week"
): string {
  return `sum(sum_over_time(carrier_edge_volte_calls${scopedKpiSelector("", tenantType)}[${getPromqlRange(resolutionPeriod)}]))`;
}

function getPeakConnectionsKpiFormula(
  tenantType: "all" | TenantType = "all",
  resolutionPeriod: ServiceKpiResolutionPeriod = "1week"
): string {
  return `max(max_over_time(carrier_edge_peak_connections${scopedKpiSelector("", tenantType)}[${getPromqlRange(resolutionPeriod)}]))`;
}

function getUniqueDevicesKpiFormula(tenantType: "all" | TenantType = "all"): string {
  return `sum(private_edge_unique_devices${scopedKpiSelector("", tenantType)})`;
}

function getUploadTonnageEXKpiFormula(
  tenantType: "all" | TenantType = "all",
  resolutionPeriod: ServiceKpiResolutionPeriod = "1week"
): string {
  return `sum(sum_over_time(private_edge_upload_bytes${scopedKpiSelector("", tenantType)}[${getPromqlRange(resolutionPeriod)}]))`;
}

function getDownloadTonnageEXKpiFormula(
  tenantType: "all" | TenantType = "all",
  resolutionPeriod: ServiceKpiResolutionPeriod = "1week"
): string {
  return `sum(sum_over_time(private_edge_download_bytes${scopedKpiSelector("", tenantType)}[${getPromqlRange(resolutionPeriod)}]))`;
}

/** CX (carrier edges): upload/download tonnage, VoLTE calls, peak connections. */
const KPI_CARRIER_EXTENSION_KEYS = [
  "uploadTonnage",
  "downloadTonnage",
  "volteCalls",
  "peakConnections",
] as const;

/** EX (private edges): unique devices, private upload/download tonnage. */
const KPI_ENTERPRISE_EXTENSION_KEYS = [
  "uniqueDevices",
  "uploadTonnageEX",
  "downloadTonnageEX",
] as const;

const KPI_EXTENSION_FORMULA_BUILDERS: Record<
  (typeof KPI_CARRIER_EXTENSION_KEYS)[number] | (typeof KPI_ENTERPRISE_EXTENSION_KEYS)[number],
  (tenantType: "all" | TenantType, resolutionPeriod: ServiceKpiResolutionPeriod) => string
> = {
  uploadTonnage: getUploadTonnageKpiFormula,
  downloadTonnage: getDownloadTonnageKpiFormula,
  volteCalls: getVolteCallsKpiFormula,
  peakConnections: getPeakConnectionsKpiFormula,
  uniqueDevices: getUniqueDevicesKpiFormula,
  uploadTonnageEX: getUploadTonnageEXKpiFormula,
  downloadTonnageEX: getDownloadTonnageEXKpiFormula,
};

/** Which extension formula groups are requested for the selected cloud (customer) type. */
export type KpiFormulaScope = {
  includeCarrierExtension: boolean;
  includeEnterpriseExtension: boolean;
};

export function getKpiFormulaScope(
  tenantType: "all" | TenantType = "all"
): KpiFormulaScope {
  if (tenantType === "EX") {
    return { includeCarrierExtension: false, includeEnterpriseExtension: true };
  }

  if (tenantType === "CX" || tenantType === "CX-KPI") {
    return { includeCarrierExtension: true, includeEnterpriseExtension: false };
  }

  return { includeCarrierExtension: true, includeEnterpriseExtension: true };
}

/** PromQL strings to request for the selected cloud (customer) type filter. */
export function getKpiFormulasForCloudType(
  tenantType: "all" | TenantType = "all",
  resolutionPeriod: ServiceKpiResolutionPeriod = "1week"
): string[] {
  const base = KPI_BASE_FORMULA_KEYS.map((key) => KPI_BASE_FORMULA_BUILDERS[key](tenantType));
  const scope = getKpiFormulaScope(tenantType);

  const formulas: string[] = [...base];
  if (scope.includeCarrierExtension) {
    formulas.push(
      ...KPI_CARRIER_EXTENSION_KEYS.map((key) => KPI_EXTENSION_FORMULA_BUILDERS[key](tenantType, resolutionPeriod))
    );
  }
  if (scope.includeEnterpriseExtension) {
    formulas.push(
      ...KPI_ENTERPRISE_EXTENSION_KEYS.map((key) => KPI_EXTENSION_FORMULA_BUILDERS[key](tenantType, resolutionPeriod))
    );
  }
  return formulas;
}

/** All KPI formulas for fetch; base and extension metrics all respect Mobile Cloud Type and Aggregation Period. */
export function getKpiFetchFormulas(
  tenantType: "all" | TenantType = "all",
  resolutionPeriod: ServiceKpiResolutionPeriod = "1week"
): string[] {
  const base = KPI_BASE_FORMULA_KEYS.map((key) => KPI_BASE_FORMULA_BUILDERS[key](tenantType));
  return [
    ...base,
    ...KPI_CARRIER_EXTENSION_KEYS.map((key) => KPI_EXTENSION_FORMULA_BUILDERS[key](tenantType, resolutionPeriod)),
    ...KPI_ENTERPRISE_EXTENSION_KEYS.map((key) => KPI_EXTENSION_FORMULA_BUILDERS[key](tenantType, resolutionPeriod)),
  ];
}

export type AggregateMetricResponse = {
  metric: string;
  metricData: [number, number][];
};

type ServiceKpiKey = keyof ServiceKPIs;

type TenantInfo = {
  name: string;
  tenantType: TenantType;
};

const KPI_MATCHERS: Array<[ServiceKpiKey, (metric: string) => boolean]> = [
  ["vmc", (metric) => metric.includes("service_instance_info")],
  ["edges", (metric) => metric.includes("edge_info")],
  ["radios", (metric) => metric.includes("radio_info")],
  ["devices", (metric) => metric.includes("device_info") && !metric.includes('status="CONNECTED"')],
  ["sites", (metric) => metric.includes("radio_site_info")],
  ["indoorSqft", (metric) => metric.includes("indoor_coverage_area_info")],
  ["outdoorSqft", (metric) => metric.includes("outdoor_coverage_area_info")],
  ["sqft", (metric) => metric.includes("coverage_area_info")],
  ["devicesConnected", (metric) => metric.includes("device_info") && metric.includes('status="CONNECTED"')],
  ["uniqueDevices", (metric) => metric.includes("private_edge_unique_devices")],
  ["uploadTonnage", (metric) => metric.includes("carrier_edge_upload_bytes")],
  ["downloadTonnage", (metric) => metric.includes("carrier_edge_download_bytes")],
  ["uploadTonnageEX", (metric) => metric.includes("private_edge_upload_bytes")],
  ["downloadTonnageEX", (metric) => metric.includes("private_edge_download_bytes")],
  ["volteCalls", (metric) => metric.includes("carrier_edge_volte_calls")],
  ["peakConnections", (metric) => metric.includes("carrier_edge_peak_connections")],
];


export function emptyAggregate(): ServiceKPIs {
  return {
    vmc: [],
    edges: [],
    radios: [],
    devices: [],
    sqft: [],
    indoorSqft: [],
    outdoorSqft: [],
    sites: [],
    devicesConnected: [],
    uniqueDevices: [],
    uploadTonnage: [],
    downloadTonnage: [],
    uploadTonnageEX: [],
    downloadTonnageEX: [],
    volteCalls: [],
    peakConnections: [],
  };
}

export function mergeMetricSeries(seriesList: MetricSeriesPoint[][]): MetricSeriesPoint[] {
  const totals = new Map<number, number>();
  for (const series of seriesList) {
    for (const [timestamp, value] of series || []) {
      totals.set(timestamp, (totals.get(timestamp) || 0) + (Number(value) || 0));
    }
  }
  return [...totals.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([timestamp, value]) => [timestamp, value]);
}

/** API metricData: [[unixSeconds, value], ...] → Highcharts [[ms, value], ...] */
export function mapMetricSeriesForChart(series: MetricSeriesPoint[] | undefined): MetricSeriesPoint[] {
  if (!Array.isArray(series)) return [];
  return series.map(([timestamp, value]) => [
    toEpochMillisForDisplay(Number(timestamp)),
    Number(value) || 0,
  ]);
}

/** Resolves aggregate KPI rows for chart components from API metrics + active filters. */
export function getKpiChartAggregate(
  metrics: ServiceKpiMetrics | null | undefined,
  tenantIds: string[],
  viewMode: AggregationMode = "all"
): ServiceKPIs | undefined {
  if (!metrics) return undefined;

  if (viewMode === "per-customer" && metrics.tenants) {
    const customersToAggregate =
      tenantIds.length > 0
        ? tenantIds.filter((id) => metrics.tenants?.[id])
        : Object.keys(metrics.tenants);
    const selectedTenants = customersToAggregate
      .map((customerId) => metrics.tenants?.[customerId])
      .filter((tenant): tenant is NonNullable<typeof tenant> => Boolean(tenant));

    if (selectedTenants.length === 0) return metrics.aggregate;

    return {
      vmc: mergeMetricSeries(selectedTenants.map((tenant) => tenant.vmc)),
      edges: mergeMetricSeries(selectedTenants.map((tenant) => tenant.edges)),
      radios: mergeMetricSeries(selectedTenants.map((tenant) => tenant.radios)),
      devices: mergeMetricSeries(selectedTenants.map((tenant) => tenant.devices)),
      sqft: mergeMetricSeries(selectedTenants.map((tenant) => tenant.sqft)),
      indoorSqft: mergeMetricSeries(selectedTenants.map((tenant) => tenant.indoorSqft)),
      outdoorSqft: mergeMetricSeries(selectedTenants.map((tenant) => tenant.outdoorSqft)),
      sites: mergeMetricSeries(selectedTenants.map((tenant) => tenant.sites)),
      devicesConnected: mergeMetricSeries(selectedTenants.map((tenant) => tenant.devicesConnected)),
      uniqueDevices: mergeMetricSeries(selectedTenants.map((tenant) => tenant.uniqueDevices)),
      uploadTonnage: mergeMetricSeries(selectedTenants.map((tenant) => tenant.uploadTonnage)),
      downloadTonnage: mergeMetricSeries(selectedTenants.map((tenant) => tenant.downloadTonnage)),
      uploadTonnageEX: mergeMetricSeries(selectedTenants.map((tenant) => tenant.uploadTonnageEX)),
      downloadTonnageEX: mergeMetricSeries(selectedTenants.map((tenant) => tenant.downloadTonnageEX)),
      volteCalls: mergeMetricSeries(selectedTenants.map((tenant) => tenant.volteCalls)),
      peakConnections: mergeMetricSeries(selectedTenants.map((tenant) => tenant.peakConnections)),
    };
  }

  if (tenantIds.length > 0 && metrics.tenants) {
    const selectedTenants = tenantIds
      .map((id) => metrics.tenants?.[id])
      .filter((tenant): tenant is NonNullable<typeof tenant> => Boolean(tenant));

    if (selectedTenants.length === 0) return metrics.aggregate;

    return {
      vmc: mergeMetricSeries(selectedTenants.map((tenant) => tenant.vmc)),
      edges: mergeMetricSeries(selectedTenants.map((tenant) => tenant.edges)),
      radios: mergeMetricSeries(selectedTenants.map((tenant) => tenant.radios)),
      devices: mergeMetricSeries(selectedTenants.map((tenant) => tenant.devices)),
      sqft: mergeMetricSeries(selectedTenants.map((tenant) => tenant.sqft)),
      indoorSqft: mergeMetricSeries(selectedTenants.map((tenant) => tenant.indoorSqft)),
      outdoorSqft: mergeMetricSeries(selectedTenants.map((tenant) => tenant.outdoorSqft)),
      sites: mergeMetricSeries(selectedTenants.map((tenant) => tenant.sites)),
      devicesConnected: mergeMetricSeries(selectedTenants.map((tenant) => tenant.devicesConnected)),
      uniqueDevices: mergeMetricSeries(selectedTenants.map((tenant) => tenant.uniqueDevices)),
      uploadTonnage: mergeMetricSeries(selectedTenants.map((tenant) => tenant.uploadTonnage)),
      downloadTonnage: mergeMetricSeries(selectedTenants.map((tenant) => tenant.downloadTonnage)),
      uploadTonnageEX: mergeMetricSeries(selectedTenants.map((tenant) => tenant.uploadTonnageEX)),
      downloadTonnageEX: mergeMetricSeries(selectedTenants.map((tenant) => tenant.downloadTonnageEX)),
      volteCalls: mergeMetricSeries(selectedTenants.map((tenant) => tenant.volteCalls)),
      peakConnections: mergeMetricSeries(selectedTenants.map((tenant) => tenant.peakConnections)),
    };
  }

  return metrics.aggregate;
}

/** Upload/download tonnage for the chart, combining carrier + private when cloud type is all. */
export function getTonnageChartSeries(
  aggregate: ServiceKPIs | undefined,
  tenantType: "all" | TenantType
): { upload: MetricSeriesPoint[]; download: MetricSeriesPoint[] } {
  if (!aggregate) {
    return { upload: [], download: [] };
  }

  const uploadRaw =
    tenantType === "EX" || tenantType === "CX-KPI"
      ? aggregate.uploadTonnageEX
      : tenantType === "CX"
        ? aggregate.uploadTonnage
        : mergeMetricSeries([aggregate.uploadTonnage, aggregate.uploadTonnageEX]);

  const downloadRaw =
    tenantType === "EX" || tenantType === "CX-KPI"
      ? aggregate.downloadTonnageEX
      : tenantType === "CX"
        ? aggregate.downloadTonnage
        : mergeMetricSeries([aggregate.downloadTonnage, aggregate.downloadTonnageEX]);

  return {
    upload: mapMetricSeriesForChart(uploadRaw),
    download: mapMetricSeriesForChart(downloadRaw),
  };
}

export function getVmcServiceType(serviceInstance: ServiceInstance): string {
  return serviceInstance.runtimeInfo?.serviceType?.[0] ?? "EX";
}

// Maps a returned PromQL metric string back to the KPI key used by the UI state.
function getKpiKey(metric: string): ServiceKpiKey | undefined {
  return KPI_MATCHERS.find(([, matches]) => matches(metric))?.[0];
}

function fillMetricSeries(
  metricData: AggregateMetricResponse["metricData"],
  start: number,
  end: number,
  resolutionSeconds: number = DEFAULT_RESOLUTION_SECONDS
) {
  const data = metricData || [];
  const alignedStart = Math.floor(start / resolutionSeconds) * resolutionSeconds;
  const endBound = Math.min(end, data.length > 0 ? data[data.length - 1][0] : end);

  // Keep API bucket timestamps as-is (converted to ms for charts). Do not re-grid via
  // fillTimeSeriesData — that emits synthetic interval indices and shifts tooltip/x-axis dates.
  return data
    .filter(([ts]) => ts >= alignedStart && ts <= endBound)
    .map(([ts, val]) => [ts * 1000, Math.round(Number(val) || 0)] as [number, number]);
}

export function buildAggregateFromMetrics(
  data: AggregateMetricResponse[],
  start: number,
  end: number,
  resolutionSeconds: number = DEFAULT_RESOLUTION_SECONDS
): ServiceKPIs {
  const aggregate = emptyAggregate();

  for (const series of data) {
    const kpiKey = getKpiKey(series.metric);
    if (kpiKey) {
      // Backfills missing intervals so charts get a complete time series.
      aggregate[kpiKey].push(...fillMetricSeries(series.metricData, start, end, resolutionSeconds));
    }
  }

  return aggregate;
}

export function buildTenantInfoById(serviceInstances: ServiceInstance[]): Map<string, TenantInfo> {
  const tenantInfoById = new Map<string, TenantInfo>();

  for (const tenant of serviceInstances) {
    if (!tenantInfoById.has(tenant.tenantId)) {
      tenantInfoById.set(tenant.tenantId, {
        name: tenant.tenantName || tenant.tenantId,
        tenantType: (getVmcServiceType(tenant) as TenantType) ?? "EX",
      });
    }
  }

  return tenantInfoById;
}

export function getTenantMspId(msp?: Tenant["msp"]): string {
  return msp?.id?.trim() ?? "";
}

export function buildTenantMspIdById(tenants: Pick<Tenant, "id" | "msp">[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const tenant of tenants) {
    if (tenant.id) {
      map.set(tenant.id, getTenantMspId(tenant.msp));
    }
  }
  return map;
}

/** Applies Managed By rules: all, no MSP (Highway9), or a specific MSP id. */
export function tenantMatchesManagedBy(tenantMspId: string, managedBy: ManagedByFilter): boolean {
  if (managedBy === "all") return true;
  if (managedBy === "highway9") return tenantMspId.length === 0;
  return tenantMspId === managedBy;
}

export function filterTenantsByCustomerType(
  tenants: TenantTree[],
  tenantType: "all" | TenantType
): TenantTree[] {
  if (tenantType === "all") return tenants;
  return tenants.filter((tenant) => tenant.serviceType?.includes(tenantType) ?? false);
}

export function filterTenantsByManagedBy(
  tenants: TenantTree[],
  managedBy: ManagedByFilter,
  tenantMspById: Map<string, string>
): TenantTree[] {
  return tenants.filter((tenant) => {
    const mspId = tenantMspById.get(tenant.id) ?? "";
    return tenantMatchesManagedBy(mspId, managedBy);
  });
}

/** Keeps VMCs for the given tenant ids; empty list means no tenant scope filter. */
export function filterServiceInstancesByTenantIds(
  instances: ServiceInstance[],
  tenantIds: string[]
): ServiceInstance[] {
  if (tenantIds.length === 0) return instances;
  const allowed = new Set(tenantIds);
  return instances.filter((si) => allowed.has(si.tenantId));
}

/** Keeps VMCs whose own runtimeInfo.serviceType matches the Mobile Cloud Type filter. */
export function filterServiceInstancesByCustomerType(
  instances: ServiceInstance[],
  tenantType: "all" | TenantType
): ServiceInstance[] {
  if (tenantType === "all") return instances;
  return instances.filter((si) => si.runtimeInfo?.serviceType?.includes(tenantType) ?? false);
}

export function buildQueryKeys(
  serviceInstances: ServiceInstance[],
  selectedTenantIds?: string[],
  managedBy: ManagedByFilter = "all",
  selectedVmcId = "all"
): { queryKeys: Array<{ key: string; value: string }>; noResults: boolean } {
  const queryKeys: Array<{ key: string; value: string }> = [];

  const tenantFilterIds = (selectedTenantIds || []).filter(Boolean);
  if (tenantFilterIds.length > 0) {
    const allTenantIds = new Set(serviceInstances.map((si) => si.tenantId));
    const validIds = tenantFilterIds.filter((id) => allTenantIds.has(id));
    if (validIds.length === 0) {
      return { queryKeys, noResults: true };
    }
    queryKeys.push({ key: "tenantId", value: validIds.join("|") });
  }

  if (managedBy !== "all") {
    queryKeys.push({
      key: "mspId",
      value: managedBy === "highway9" ? "" : managedBy,
    });
  }

  if (selectedVmcId !== "all") {
    const vmcExists = serviceInstances.some((si) => si.id === selectedVmcId);
    if (!vmcExists) {
      return { queryKeys, noResults: true };
    }
    queryKeys.push({ key: "service_instance_id", value: selectedVmcId });
  }

  return { queryKeys, noResults: false };
}

export type { ServiceKpiDisplayMetricKey } from "~/types/serviceKpi";

export type ServiceKpiExtensionChartKey = "volteCalls" | "peakConnections" | "uniqueDevices";

export type ServiceKpiStackedSeries = {
  dataKey: keyof ServiceKPIs;
  label: string;
  color: string;
};

export type ServiceKpiDisplayMetric = {
  key: ServiceKpiDisplayMetricKey;
  dataKey?: keyof ServiceKPIs;
  /** When set, Growth Trends renders a stacked column chart for these series. */
  stackedSeries?: ServiceKpiStackedSeries[];
  label: string;
  yLabel: string;
  tableValueSuffix?: string;
  tenantTypes: TenantType[];
};

export function resolveStackedGrowthSeries(
  metrics: ServiceKpiMetrics,
  tenantIds: string[],
  stackedSeries: ServiceKpiStackedSeries[]
): MetricSeriesPoint[][] {
  const resolveKey = (dataKey: keyof ServiceKPIs): MetricSeriesPoint[] => {
    if (tenantIds.length > 0 && metrics.tenants) {
      const selectedTenants = tenantIds
        .map((id) => metrics.tenants?.[id])
        .filter((tenant): tenant is NonNullable<typeof tenant> => Boolean(tenant));
      if (selectedTenants.length > 0) {
        return mergeMetricSeries(
          selectedTenants.map((tenant) => (tenant[dataKey] as MetricSeriesPoint[]) ?? [])
        );
      }
    }
    return (metrics.aggregate?.[dataKey] as MetricSeriesPoint[] | undefined) ?? [];
  };

  return stackedSeries.map((series) => resolveKey(series.dataKey));
}

export const SERVICE_KPI_SQFT_SUFFIX = " sq.ft";

export const SERVICE_KPI_DISPLAY_METRICS: ServiceKpiDisplayMetric[] = [
  {
    key: "vmc",
    dataKey: "vmc",
    label: "Mobile Clouds",
    yLabel: "Number of Mobile Clouds",
    tenantTypes: ["CX", "EX"],
  },
  {
    key: "edges",
    dataKey: "edges",
    label: "Edges",
    yLabel: "Number of Edges",
    tenantTypes: ["CX", "EX"],
  },
  {
    key: "radios",
    dataKey: "radios",
    label: "Radios",
    yLabel: "Number of Radios",
    tenantTypes: ["CX", "EX"],
  },
  {
    key: "devices",
    dataKey: "devices",
    label: "Devices",
    yLabel: "Number of Devices",
    tenantTypes: ["CX", "EX"],
  },
  {
    key: "sqft",
    dataKey: "sqft",
    label: "Sq.Ft",
    yLabel: "Square Feet",
    tableValueSuffix: SERVICE_KPI_SQFT_SUFFIX,
    tenantTypes: ["CX", "EX"],
  },
  {
    key: "coverageSqft",
    label: "Indoor / Outdoor Sq.Ft",
    yLabel: "Square Feet",
    tableValueSuffix: SERVICE_KPI_SQFT_SUFFIX,
    tenantTypes: ["CX", "EX"],
    stackedSeries: [
      { dataKey: "indoorSqft", label: "Indoor Sq.Ft", color: "#026950" },
      // Matches the Upload line color in DataTonnageChart's SERIES_COLORS.
      { dataKey: "outdoorSqft", label: "Outdoor Sq.Ft", color: "#AA4AAC" },
    ],
  },
  {
    key: "sites",
    dataKey: "sites",
    label: "Sites",
    yLabel: "Number of Sites",
    tenantTypes: ["CX", "EX"],
  },
];

/** Metrics with dedicated line charts (ExtensionKpiCharts / DataTonnageChart). */
export const SERVICE_KPI_EXTENSION_CHART_METRICS: Array<{
  key: ServiceKpiExtensionChartKey;
  dataKey: keyof ServiceKPIs;
  label: string;
  titleTooltip: string;
  tenantTypes: TenantType[];
}> = [
  {
    key: "volteCalls",
    dataKey: "volteCalls",
    label: "Volte Calls",
    titleTooltip: "Carrier Extension",
    tenantTypes: ["CX"],
  },
  {
    key: "peakConnections",
    dataKey: "peakConnections",
    label: "Peak Connections",
    titleTooltip: "Carrier Extension",
    tenantTypes: ["CX"],
  },
  {
    key: "uniqueDevices",
    dataKey: "uniqueDevices",
    label: "Unique Devices",
    titleTooltip: "Enterprise Extension",
    tenantTypes: ["EX"],
  },
];

export function getServiceKpiDisplayMetrics(
  _tenantType: "all" | TenantType = "all"
): ServiceKpiDisplayMetric[] {
  return SERVICE_KPI_DISPLAY_METRICS.filter(
    (metric) => metric.dataKey != null || (metric.stackedSeries?.length ?? 0) > 0
  );
}

/** All chart metrics for All Types/CX/CX-KPI; Volte Calls and Peak Connections are hidden for EX. */
export function getServiceKpiExtensionChartMetrics(tenantType: "all" | TenantType = "all") {
  if (tenantType === "EX") {
    return SERVICE_KPI_EXTENSION_CHART_METRICS.filter((metric) => !CX_ONLY_METRIC_KEYS.has(metric.key));
  }
  return SERVICE_KPI_EXTENSION_CHART_METRICS;
}

export type ServiceKpiTableMetric = {
  key: string;
  dataKey: keyof ServiceKPIs;
  label: string;
  tableValueSuffix?: string;
  tableFormat?: "number" | "bytes";
};

/** Rows for the summary table; filtered by getServiceKpiTableMetrics. */
export const SERVICE_KPI_TABLE_METRICS: ServiceKpiTableMetric[] = [
  { key: "vmc", dataKey: "vmc", label: "Mobile Clouds" },
  { key: "edges", dataKey: "edges", label: "Edges" },
  { key: "radios", dataKey: "radios", label: "Radios" },
  { key: "devices", dataKey: "devices", label: "Devices" },
  {
    key: "sqft",
    dataKey: "sqft",
    label: "Sq.Ft",
    tableValueSuffix: SERVICE_KPI_SQFT_SUFFIX,
  },
  {
    key: "indoorSqft",
    dataKey: "indoorSqft",
    label: "Indoor Sq.Ft",
    tableValueSuffix: " sq.ft",
  },
  {
    key: "outdoorSqft",
    dataKey: "outdoorSqft",
    label: "Outdoor Sq.Ft",
    tableValueSuffix: " sq.ft",
  },
  { key: "sites", dataKey: "sites", label: "Sites" },
  {
    key: "uploadTonnage",
    dataKey: "uploadTonnage",
    label: "Upload Tonnage (CX)",
    tableFormat: "bytes",
  },
  {
    key: "downloadTonnage",
    dataKey: "downloadTonnage",
    label: "Download Tonnage (CX)",
    tableFormat: "bytes",
  },
  {
    key: "volteCalls",
    dataKey: "volteCalls",
    label: "Volte Calls",
  },
  {
    key: "peakConnections",
    dataKey: "peakConnections",
    label: "Peak Connections",
  },
  {
    key: "uniqueDevices",
    dataKey: "uniqueDevices",
    label: "Unique Devices",
  },
  {
    key: "uploadTonnageEX",
    dataKey: "uploadTonnageEX",
    label: "Upload Tonnage (EX)",
    tableFormat: "bytes",
  },
  {
    key: "downloadTonnageEX",
    dataKey: "downloadTonnageEX",
    label: "Download Tonnage (EX)",
    tableFormat: "bytes",
  },
];

/** Carrier Extension (CX) only metrics; hidden from the table/tonnage chart when EX is selected. */
const CX_ONLY_METRIC_KEYS = new Set(["volteCalls", "peakConnections", "uploadTonnage", "downloadTonnage"]);

/** All table rows for All Types/CX/CX-KPI; Volte Calls and Peak Connections are hidden for EX. */
export function getServiceKpiTableMetrics(
  tenantType: "all" | TenantType = "all"
): ServiceKpiTableMetric[] {
  if (tenantType === "EX") {
    return SERVICE_KPI_TABLE_METRICS.filter((metric) => !CX_ONLY_METRIC_KEYS.has(metric.key));
  }
  return SERVICE_KPI_TABLE_METRICS;
}

const KB = 1024;
const MB = 1024 * KB;
const GB = 1024 * MB;

export function formatKpiTableBytes(bytes: number, decimal = 1): string {
  if (!Number.isFinite(bytes)) return "N/A";
  if (bytes < KB) return `${bytes.toFixed(decimal)} B`;
  if (bytes < MB) return `${(bytes / KB).toFixed(decimal)} KB`;
  if (bytes < GB) return `${(bytes / MB).toFixed(decimal)} MB`;
  return `${(bytes / GB).toFixed(2)} GB`;
}

function formatTruncatedCompactPart(value: number, divisor: number, suffix: string): string {
  const truncated = Math.floor(value / (divisor / 10)) / 10;
  return Number.isInteger(truncated) ? `${truncated}${suffix}` : `${truncated.toFixed(1)}${suffix}`;
}

export function formatKpiCompactCount(value: number, suffix = ""): string {
  if (!Number.isFinite(value)) return "N/A";

  const absValue = Math.abs(value);
  const sign = value < 0 ? "-" : "";

  if (absValue < 1_000) {
    return sign + absValue.toLocaleString() + suffix;
  }
  if (absValue < 1_000_000) {
    return sign + formatTruncatedCompactPart(absValue, 1_000, "K") + suffix;
  }
  if (absValue < 1_000_000_000) {
    return sign + formatTruncatedCompactPart(absValue, 1_000_000, "M") + suffix;
  }
  return sign + formatTruncatedCompactPart(absValue, 1_000_000_000, "B") + suffix;
}

export function getDefaultServiceKpiDisplayMetric(
  _tenantType: "all" | TenantType = "all"
): ServiceKpiDisplayMetricKey {
  const metrics = getServiceKpiDisplayMetrics();
  return metrics.find((m) => m.key === "vmc")?.key ?? metrics[0]?.key ?? "vmc";
}
